// client/src/pages/NexusHub.js
// ✅ asset-ready gate + loader overlay + “UI-visible” ready flag + container fade-in class
// ✅ Community (Manager) tile HARD-CODED to open Metricool login

import React, { useEffect, useState, useCallback, useRef } from "react";
import "./NexusHub.css";

import Loading from "../components/Loading";
import CphereHeaderLogo from "../components/CphereHeaderLogo";

import backgroundImage from "../assets/background.jpg"; // ✅ preload CSS background too

// Tile images
import creativeStudioImage from "../assets/tiles/creativestudio.tile.jpg"; // Creative
import creativeTraffickingImage from "../assets/tiles/creativetraffickingNEW.tile.jpg"; // Traffick
import creativeExecutionImage from "../assets/govern.gif"; // Execution
import creativeOptimusImage from "../assets/tiles/creativeAnalysis.tile.jpg";
import communityManagementImage from "../assets/tiles/communitymanagement.tile.jpg"; // Social (Manager)

import { useNavigate } from "react-router-dom";
import {
  BETA_SURFACE_OPERATED_BY_CPHERE_COPY,
  BETA_SURFACE_UNAVAILABLE_COPY,
} from "../config/betaSurfaceAccess";

/* ✅ module-level cache so returning to NexusHub is smooth */
let CLIENT_NEXUS_ASSETS_WARM = false;

function preloadImage(url, timeoutMs = 4500) {
  return new Promise((resolve) => {
    if (!url) return resolve();

    const img = new Image();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const timer = setTimeout(finish, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      // decode() is best-effort; GIFs often don’t decode meaningfully
      if (typeof img.decode === "function") {
        img.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };

    img.onerror = () => {
      clearTimeout(timer);
      finish();
    };

    img.src = url;
  });
}

const NexusHub = () => {
  const navigate = useNavigate();

  // "assetsReady" = assets are warmed
  const [assetsReady, setAssetsReady] = useState(CLIENT_NEXUS_ASSETS_WARM);

  // "fadeIn" = ready shell should fade to opacity 1
  const [fadeIn, setFadeIn] = useState(false);

  // ✅ "uiReady" = UI is actually visible (loader can go away)
  const [uiReady, setUiReady] = useState(CLIENT_NEXUS_ASSETS_WARM);

  // Roadmap-preview tiles. These name protected Beta surfaces the account
  // cannot open, so a press states that plainly instead of navigating. No
  // route transition, no href, no prefetch, no component mount, no request —
  // the surface behind them is denied at the route boundary and again by the
  // backend, and the tile must not pretend otherwise.
  const [previewNotice, setPreviewNotice] = useState("");
  const previewNoticeTimerRef = useRef(null);

  const [previewNoticeCopy, setPreviewNoticeCopy] = useState(BETA_SURFACE_UNAVAILABLE_COPY);
  const showPreviewNotice = useCallback((surfaceLabel, copy = BETA_SURFACE_UNAVAILABLE_COPY) => {
    setPreviewNoticeCopy(copy);
    setPreviewNotice(surfaceLabel);
    if (previewNoticeTimerRef.current) {
      clearTimeout(previewNoticeTimerRef.current);
    }
    previewNoticeTimerRef.current = setTimeout(() => setPreviewNotice(""), 4200);
  }, []);

  useEffect(
    () => () => {
      if (previewNoticeTimerRef.current) {
        clearTimeout(previewNoticeTimerRef.current);
      }
    },
    []
  );

  const uiReadyTimeoutRef = useRef(null);

  const handleLogoClick = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  // ✅ HARD-CODED: open Metricool login (no routing, no advertiser link logic)
  const handleSocialClick = useCallback(() => {
    window.open(
      "https://app.metricool.com/login",
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (CLIENT_NEXUS_ASSETS_WARM) {
      setAssetsReady(true);
      setUiReady(true);
      return;
    }

    const urls = [
      backgroundImage, // ✅ ensure fixed background is cached before reveal
      creativeStudioImage,
      creativeTraffickingImage,
      creativeExecutionImage,
      creativeOptimusImage,
      communityManagementImage,
    ];

    (async () => {
      try {
        await Promise.all(urls.map((u) => preloadImage(u)));
      } finally {
        if (cancelled) return;
        CLIENT_NEXUS_ASSETS_WARM = true;
        setAssetsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // If we re-gate, reset visibility
    if (!assetsReady) {
      setFadeIn(false);
      setUiReady(false);
      return;
    }

    // Start fade-in on next paint
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFadeIn(true));
    });

    // Fallback: if transitionend doesn't fire (reduced motion / browser quirks)
    if (uiReadyTimeoutRef.current) clearTimeout(uiReadyTimeoutRef.current);
    uiReadyTimeoutRef.current = setTimeout(() => {
      setUiReady(true);
    }, 450); // > 220ms transition + buffer

    return () => {
      cancelAnimationFrame(raf);
      if (uiReadyTimeoutRef.current) clearTimeout(uiReadyTimeoutRef.current);
    };
  }, [assetsReady]);

  const handleReadyShellTransitionEnd = useCallback((e) => {
    // only treat opacity transition as “UI now visible”
    if (e?.propertyName !== "opacity") return;
    setUiReady(true);
  }, []);

  return (
    <div
      className={`nexushub-page-container ${
        uiReady ? "nexushub-is-ready" : ""
      }`}
    >
      {/* ✅ Loader stays mounted UNTIL the UI is visibly faded-in */}
      {!uiReady && (
        <div className="nexushub-loading-shell">
          <Loading />
        </div>
      )}

      {/* ✅ Ready shell is mounted immediately but starts hidden (opacity:0) */}
      <div
        className={`nexushub-ready-shell ${fadeIn ? "nexushub-fade-in" : ""}`}
        aria-hidden={!assetsReady}
        onTransitionEnd={handleReadyShellTransitionEnd}
      >
        <div className="nexushub-page-content">
          {/* Shared Cphere header logo */}
          <CphereHeaderLogo
            alt="Cphere"
            className="nexushub-poweredby-logo"
            onClick={handleLogoClick}
            style={{ cursor: "pointer" }}
          />

          {/* Hero */}
          <div className="nexushub-hero-container">
            <div className="nexushub-hero-pill">
              <span className="nexushub-hero-pill-label cphere-hero-pill-wordmark">Creative</span>
              <span className="nexushub-hero-pill-tag cphere-hero-pill-tag">Studio</span>
            </div>
            <div className="nexushub-hero-shimmer-bar" />
          </div>

          {/* TILE GRID */}
          <div className="nexushub-tile-grid">
            {/* 1 — Creative */}
            <div
              className="nexushub-tile nexushub-creative-tile"
              onClick={() => navigate("/creative-studio")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/creative-studio");
                }
              }}
            >
              <div
                className="nexushub-tile-face"
                style={{ backgroundImage: `url(${creativeStudioImage})` }}
              >
                <span className="nexushub-beta-tag">Design</span>
                <span className="nexushub-tile-label">Creative</span>
              </div>
            </div>

            {/* 2 — Creative (Operations) */}
            <div
              className="nexushub-tile nexushub-trafficking-tile nexushub-tile--preview"
              role="button"
              tabIndex={0}
              aria-label="Creative Operations — operated by Cphere"
              onClick={() => showPreviewNotice("Creative Operations", BETA_SURFACE_OPERATED_BY_CPHERE_COPY)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  showPreviewNotice("Creative Operations", BETA_SURFACE_OPERATED_BY_CPHERE_COPY);
                }
              }}
            >
              <div
                className="nexushub-tile-face"
                style={{ backgroundImage: `url(${creativeTraffickingImage})` }}
              >
                <span className="nexushub-beta-tag nexushub-beta-tag--opposite">
                  Beta
                </span>
                <span className="nexushub-beta-tag nexushub-agentic-tag">
                  Operations
                </span>
                <span className="nexushub-tile-label">Creative</span>
              </div>
            </div>

            <div
              className="nexushub-tile nexushub-trafficking-tile nexushub-tile--preview"
              role="button"
              tabIndex={0}
              aria-label="Execution — operated by Cphere"
              onClick={() => showPreviewNotice("Execution", BETA_SURFACE_OPERATED_BY_CPHERE_COPY)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  showPreviewNotice("Execution", BETA_SURFACE_OPERATED_BY_CPHERE_COPY);
                }
              }}
            >
              <div
                className="nexushub-tile-face"
                style={{ backgroundImage: `url(${creativeExecutionImage})` }}
              >
                <span className="nexushub-beta-tag">Governed</span>
                <span className="nexushub-beta-tag nexushub-beta-tag--opposite">
                  Beta
                </span>
                <span className="nexushub-tile-label">Execution</span>
              </div>
            </div>

            {/* 3 — Community (Manager) */}
            <div
              className="nexushub-tile nexushub-creative-tile nexushub-tile--preview"
              role="button"
              tabIndex={0}
              aria-label="Creative Insights — coming soon"
              onClick={() => showPreviewNotice("Creative Insights")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  showPreviewNotice("Creative Insights");
                }
              }}
            >
              <div
                className="nexushub-tile-face"
                style={{ backgroundImage: `url(${creativeOptimusImage})` }}
              >
                <span className="nexushub-beta-tag">Insights</span>
                <span className="nexushub-beta-tag nexushub-beta-tag--opposite">
                  Beta
                </span>
                <span className="nexushub-tile-label">Creative</span>
              </div>
            </div>

            {/* 5 — Community (Manager) */}
            <div
              className="nexushub-tile nexushub-social-tile"
              onClick={handleSocialClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSocialClick();
                }
              }}
            >
              <div
                className="nexushub-tile-face"
                style={{ backgroundImage: `url(${communityManagementImage})` }}
              >
                <span className="nexushub-beta-tag">Manager</span>
                <span className="nexushub-tile-label">Community</span>
              </div>
            </div>
          </div>

          <div className="nexushub-preview-notice-slot" role="status" aria-live="polite">
            {previewNotice ? (
              <p className="nexushub-preview-notice">
                <span className="nexushub-preview-notice-surface">{previewNotice}</span>
                {` — ${previewNoticeCopy}`}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NexusHub;
